import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Nav, Row } from 'react-bootstrap';
import SimpleMDE from 'react-simplemde-editor';
import { Lock } from 'react-feather';
import { toast } from 'react-toastify';
import {
  getBrandingAPI,
  getCompanyTemplateDetailAPI,
  publishCompanyTemplateAPI,
  saveCompanyTemplateAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import { companyUrl } from '../../../utils/mhNav';
import 'easymde/dist/easymde.min.css';
import styles from './TemplateEditor.module.scss';

const LANGS = [
  { key: 'en', label: 'English' },
  { key: 'ar', label: 'Arabic' },
  { key: 'fr', label: 'French' },
];

export default function CompanyTemplateEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromId = searchParams.get('from');
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [branding, setBranding] = useState(null);
  const [lang, setLang] = useState('en');
  const [localId, setLocalId] = useState(isNew ? null : id);

  const mdeOptions = useMemo(() => ({ spellChecker: false, minHeight: '200px' }), []);

  const load = useCallback(async () => {
    if (isNew && !fromId) {
      setLoading(true);
      try {
        const [b, t] = await Promise.all([getBrandingAPI(), getCompanyTemplateDetailAPI('new')]);
        setBranding(b.data);
        setDetail(t.data);
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [b, t] = await Promise.all([
        getBrandingAPI(),
        getCompanyTemplateDetailAPI(isNew ? fromId : id),
      ]);
      setBranding(b.data);
      setDetail(t.data);
      setLocalId(t.data.id || id);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id, isNew, fromId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateLangField = (field, value) => {
    setDetail((d) => ({
      ...d,
      languages: {
        ...d.languages,
        [lang]: { ...d.languages[lang], [field]: value },
      },
    }));
  };

  const copyFromEn = () => {
    if (!detail?.languages?.en) return;
    setDetail((d) => ({
      ...d,
      languages: {
        ...d.languages,
        [lang]: { ...d.languages[lang], ...d.languages.en, rtl: lang === 'ar' },
      },
    }));
    toast.success('Copied from English');
  };

  const save = async () => {
    const sid = localId || id || 'new';
    const res = await saveCompanyTemplateAPI(sid === 'new' ? 'new' : sid, detail);
    toast.success('Saved');
    const nid = res.data?.id;
    if (isNew && nid) {
      navigate(companyUrl(`templates/${nid}`), { replace: true });
      setLocalId(nid);
    }
  };

  const publish = async () => {
    const en = detail?.languages?.en;
    if (!en?.subject?.trim() || !en?.bodyMarkdown?.trim()) {
      toast.error('English subject and body are required to publish');
      return;
    }
    await publishCompanyTemplateAPI(localId || id);
    toast.success('Published');
    load();
  };

  if (loading && !detail) {
    return (
      <div>
        <PageHeader title="Template" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Templates', path: companyUrl('templates') }, { label: 'Editor' }]} />
        <LoadingSkeleton rows={6} cols={3} />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div>
        <PageHeader title="Template" breadcrumbs={[{ label: 'Company', path: companyUrl('templates') }]} />
        <p className="text-danger">Could not load template.</p>
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  const rtl = lang === 'ar' || detail.languages?.[lang]?.rtl;
  const locked = detail.lockedFields || [];

  return (
    <div>
      <PageHeader
        title={isNew ? 'New template' : detail.name}
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Templates', path: companyUrl('templates') },
          { label: isNew ? 'New' : 'Edit' },
        ]}
        secondaryActions={[{ label: 'Back', onClick: () => navigate(companyUrl('templates')) }]}
      />

      <Row className="g-3">
        <Col lg={8}>
          <Card className="mb-3">
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Label>Name</Form.Label>
                <Form.Control value={detail.name} onChange={(e) => setDetail({ ...detail, name: e.target.value })} />
              </Form.Group>
              <div className="d-flex flex-wrap gap-2 mb-2">
                {locked.map((f) => (
                  <Badge key={f} bg="light" text="dark" className="border">
                    <Lock size={12} className="me-1" />
                    {f} (locked)
                  </Badge>
                ))}
              </div>
              <Nav variant="tabs" className="mb-2">
                {LANGS.map((L) => (
                  <Nav.Item key={L.key}>
                    <Nav.Link active={lang === L.key} onClick={() => setLang(L.key)} style={{ cursor: 'pointer' }}>
                      {L.label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              {lang !== 'en' && (
                <Button size="sm" variant="outline-secondary" className="mb-2" onClick={copyFromEn}>
                  Copy from EN
                </Button>
              )}
              <Form.Group className="mb-2">
                <Form.Label>Subject</Form.Label>
                <Form.Control
                  value={detail.languages?.[lang]?.subject || ''}
                  onChange={(e) => updateLangField('subject', e.target.value)}
                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                />
              </Form.Group>
              <div dir={rtl && lang === 'ar' ? 'rtl' : 'ltr'} key={lang}>
                <SimpleMDE
                  value={detail.languages?.[lang]?.bodyMarkdown || ''}
                  onChange={(v) => updateLangField('bodyMarkdown', v)}
                  options={mdeOptions}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card>
            <Card.Body>
              <h6>Brand preview</h6>
              <div
                className={styles.previewBox}
                style={{
                  borderColor: branding?.primaryColor || '#644998',
                  background: `linear-gradient(180deg, ${branding?.primaryColor || '#644998'}22, transparent)`,
                }}
              >
                <div className="fw-medium" style={{ color: branding?.primaryColor }}>
                  {branding?.portalTitle || 'Portal'}
                </div>
                <div className="small text-muted">{detail.name}</div>
              </div>
              <Alert variant="info" className="small mt-2 py-2">
                Globally locked fields are read-only in company scope (shown above).
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="d-flex gap-2">
        <Button onClick={save}>Save</Button>
        <Button variant="success" onClick={publish}>
          Publish
        </Button>
      </div>
    </div>
  );
}
